import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShieldAlert } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { logoutRequest } from '../features/auth/authApi';

/**
 * Persistent, non-dismissible bar shown while a super admin is logged in as
 * another user. "Back to Super Admin" revokes the impersonated session
 * server-side and restores the stashed admin session verbatim.
 */
export default function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.impersonation);
  const user = useAuthStore((s) => s.user);
  const endImpersonation = useAuthStore((s) => s.endImpersonation);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const back = useMutation({
    mutationFn: async () => {
      // Swap the store first, then best-effort revoke the impersonated
      // refresh session (logout is public and token-addressed).
      const impersonatedRefreshToken = endImpersonation();
      if (impersonatedRefreshToken) {
        await logoutRequest(impersonatedRefreshToken).catch(() => {});
      }
    },
    onSettled: () => {
      queryClient.clear();
      if (useAuthStore.getState().accessToken) {
        toast.success('Back in your super admin session');
        navigate('/admin/users', { replace: true });
      } else {
        // The page was reloaded, so the admin session was not kept.
        toast.success('Session ended. Sign in to the admin portal again.');
        navigate('/admin/login', { replace: true });
      }
    },
  });

  if (!impersonation) return null;

  const endsAt = impersonation.adminInfo?.expiresAt
    ? new Date(impersonation.adminInfo.expiresAt).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="z-50 flex items-center gap-3 border-b border-amber-200 bg-amber-100 px-4 py-2 text-sm text-amber-800">
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
      <span className="flex-1 truncate">
        You are logged in as{' '}
        <strong>{user?.email ?? impersonation.admin?.user?.email ?? 'a user'}</strong>. Every
        action is real.{endsAt ? ` This session ends at ${endsAt}.` : ''}
      </span>
      <button
        onClick={() => back.mutate()}
        disabled={back.isPending}
        className="shrink-0 rounded-lg border border-amber-400 px-3 py-1 font-semibold text-amber-900 hover:bg-amber-200 disabled:opacity-50"
      >
        Back to Super Admin
      </button>
    </div>
  );
}
